/**
 * Input Sanitization Utility
 * Provides comprehensive XSS protection for form inputs
 *
 * Security Features:
 * - HTML/script tag filtering
 * - Event handler blocking (onclick, onerror, onload, etc.)
 * - JavaScript protocol filtering
 * - Data URI filtering
 * - HTML entity encoding
 * - Performance-optimized with memoization
 *
 * Usage:
 * ```
 * const sanitized = sanitizeInput(userInput);
 * const sanitized = sanitizeInput(userInput, { strict: true });
 * ```
 */

/**
 * Configuration options for sanitization
 */
export interface SanitizeOptions {
  /** Strict mode: removes all HTML, encodes entities (default: false) */
  strict?: boolean;
  /** Allow basic HTML tags like <b>, <i>, <u> (default: false) */
  allowBasicHtml?: boolean;
  /** Decode HTML entities before sanitizing (default: true) */
  decodeEntities?: boolean;
  /** Remove newlines and extra whitespace (default: false) */
  compact?: boolean;
}

/**
 * Patterns for detecting dangerous content
 */
const DANGEROUS_PATTERNS = {
  // Script tags
  scriptTag: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/i,
  // Event handlers
  eventHandlers: /\s*on(?:abort|activate|afterprint|afterupdate|beforeactivate|beforecopy|beforecut|beforedeactivate|beforeeditfocus|beforepaste|beforeprint|beforeunload|beforeupdate|blur|bounce|cellchange|change|click|contextmenu|controlselect|copy|cut|dataavailable|datasetchange|datasetcomplete|dbclick|deactivate|delete|deselect|drop|error|errorupdate|exit|filterchange|finish|focus|focusin|focusout|help|input|keydown|keypress|keyup|load|losecapture|mousedown|mouseenter|mouseleave|mousemove|mouseout|mouseover|mouseup|mousewheel|move|moveend|movestart|paste|propertychange|readystatechange|reset|resizeend|resizestart|rowenter|rowexit|rowsdelete|rowsinserted|scroll|select|selectionchange|selectstart|start|stop|submit|unload)\s*=/i,
  // JavaScript protocol
  jsProtocol: /javascript:/i,
  // Data URIs with script
  dataUriScript: /data:(?:text\/html|application\/javascript)/i,
  // Event in attribute style
  eventAttribute: /on[a-z]+\s*=/i,
  // vbscript protocol
  vbscriptProtocol: /vbscript:/i,
};

/**
 * Cache for sanitization results to improve performance
 * Uses a simple LRU cache with max 1000 entries
 */
const sanitizeCache = new Map<string, string>();
const MAX_CACHE_SIZE = 1000;

/**
 * Clear the sanitization cache
 */
export function clearSanitizeCache(): void {
  sanitizeCache.clear();
}

/**
 * Main sanitization function
 *
 * @param input - User input to sanitize
 * @param options - Sanitization options
 * @returns Sanitized string safe for rendering
 *
 * @example
 * // Basic sanitization (removes scripts, event handlers)
 * const clean = sanitizeInput('<img src=x onerror="alert(\'xss\')">');
 * // Result: '<img src=x>'
 *
 * @example
 * // Strict mode (removes all HTML)
 * const clean = sanitizeInput('<b>Bold</b>', { strict: true });
 * // Result: '&lt;b&gt;Bold&lt;/b&gt;'
 */
export function sanitizeInput(input: unknown, options: SanitizeOptions = {}): string {
  // Handle non-string inputs
  if (input === null || input === undefined) {
    return '';
  }

  if (typeof input !== 'string') {
    return String(input);
  }

  const {
    strict = false,
    allowBasicHtml = false,
    decodeEntities = true,
    compact = false,
  } = options;

  // Check cache first
  const cacheKey = `${input}|${strict}|${allowBasicHtml}|${decodeEntities}|${compact}`;
  if (sanitizeCache.has(cacheKey)) {
    return sanitizeCache.get(cacheKey) || '';
  }

  let sanitized = input;

  // Phase 1: Decode HTML entities if requested
  if (decodeEntities) {
    sanitized = decodeHtmlEntities(sanitized);
  }

  // Phase 2: Remove script tags completely
  sanitized = sanitized.replace(new RegExp(DANGEROUS_PATTERNS.scriptTag.source, 'gi'), '');

  // Phase 3: Remove event handlers
  sanitized = sanitized.replace(new RegExp(DANGEROUS_PATTERNS.eventHandlers.source, 'gi'), ' ');
  sanitized = sanitized.replace(new RegExp(DANGEROUS_PATTERNS.eventAttribute.source, 'gi'), ' ');

  // Phase 4: Remove dangerous protocols
  sanitized = sanitized.replace(new RegExp(DANGEROUS_PATTERNS.jsProtocol.source, 'gi'), '');
  sanitized = sanitized.replace(new RegExp(DANGEROUS_PATTERNS.vbscriptProtocol.source, 'gi'), '');
  sanitized = sanitized.replace(new RegExp(DANGEROUS_PATTERNS.dataUriScript.source, 'gi'), '');

  // Phase 5: In strict mode, remove all HTML tags
  if (strict) {
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  }

  // Phase 6: Compact whitespace if requested
  if (compact) {
    sanitized = sanitized.replace(/\s+/g, ' ').trim();
  }

  // Phase 7: HTML entity encoding for display safety
  sanitized = encodeHtmlEntities(sanitized);

  // Cache the result
  if (sanitizeCache.size >= MAX_CACHE_SIZE) {
    // Remove first (oldest) entry
    const firstKey = sanitizeCache.keys().next().value;
    if (firstKey) sanitizeCache.delete(firstKey);
  }
  sanitizeCache.set(cacheKey, sanitized);

  return sanitized;
}

/**
 * Validates if a string contains potentially dangerous content
 *
 * @param input - String to validate
 * @returns true if dangerous content detected, false otherwise
 *
 * @example
 * hasDangerousContent('<img onerror="alert()">') // true
 * hasDangerousContent('Hello World') // false
 */
export function hasDangerousContent(input: string): boolean {
  if (typeof input !== 'string') {
    return false;
  }

  return (
    DANGEROUS_PATTERNS.scriptTag.test(input) ||
    DANGEROUS_PATTERNS.eventHandlers.test(input) ||
    DANGEROUS_PATTERNS.jsProtocol.test(input) ||
    DANGEROUS_PATTERNS.vbscriptProtocol.test(input) ||
    DANGEROUS_PATTERNS.dataUriScript.test(input)
  );
}

/**
 * Decodes HTML entities (e.g., &lt; -> <)
 * This helps catch encoded attacks
 *
 * @internal
 */
function decodeHtmlEntities(str: string): string {
  // Create a temporary element to decode entities
  if (typeof document === 'undefined') {
    // Server-side: use simple replacements
    return str
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
}

/**
 * Encodes HTML entities to prevent interpretation
 *
 * @internal
 */
function encodeHtmlEntities(str: string): string {
  // Use Object.entries for better performance
  const entityMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return str.replace(/[&<>"']/g, (char) => entityMap[char] || char);
}

/**
 * Sanitizes HTML content for safe rendering
 * Removes all tags and returns plain text
 *
 * @param html - HTML string to sanitize
 * @returns Plain text without HTML
 *
 * @example
 * stripHtml('<div class="test">Hello</div>') // 'Hello'
 */
export function stripHtml(html: string): string {
  if (typeof html !== 'string') {
    return '';
  }

  return html
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&[a-zA-Z0-9]+;/g, '') // Remove HTML entities
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

/**
 * Validates email addresses with basic sanitization
 *
 * @param email - Email string to validate
 * @returns Sanitized email or empty string if invalid
 *
 * @example
 * sanitizeEmail('user@example.com') // 'user@example.com'
 * sanitizeEmail('<script>alert()</script>@example.com') // ''
 */
export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') {
    return '';
  }

  // Remove HTML-like content first
  const cleaned = stripHtml(email);

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(cleaned)) {
    return '';
  }

  // Additional check: no dangerous patterns
  if (hasDangerousContent(cleaned)) {
    return '';
  }

  return cleaned.toLowerCase().trim();
}

/**
 * Sanitizes URLs to prevent javascript: and data: protocols
 *
 * @param url - URL to sanitize
 * @returns Sanitized URL or empty string if dangerous
 *
 * @example
 * sanitizeUrl('https://example.com') // 'https://example.com'
 * sanitizeUrl('javascript:alert()') // ''
 * sanitizeUrl('data:text/html,...') // ''
 */
export function sanitizeUrl(url: string): string {
  if (typeof url !== 'string') {
    return '';
  }

  const trimmed = url.trim();

  // Block dangerous protocols
  if (
    /^(javascript|data|vbscript|file):/i.test(trimmed) ||
    /^about:/i.test(trimmed)
  ) {
    return '';
  }

  // Allow relative and absolute URLs
  if (trimmed.startsWith('/') || trimmed.startsWith('.')) {
    return trimmed;
  }

  // Validate absolute URLs
  try {
    const urlObj = new URL(trimmed);
    if (!['http:', 'https:', 'ftp:', 'ftps:'].includes(urlObj.protocol)) {
      return '';
    }
    return trimmed;
  } catch {
    // Invalid URL
    return '';
  }
}
